import { useRef, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import SubPageHeader from '@/components/layout/SubPageHeader';
import SubPageShell from '@/components/layout/SubPageShell';
import SubPageSection from '@/components/layout/SubPageSection';
import SubPageSurface from '@/components/layout/SubPageSurface';
import SectionHeading from '@/components/base/SectionHeading';
import { SUBPAGE_SURFACE_BORDER } from '@/constants/surfaceColors';

function FadeIn({ children, delay = 0 }: { children: React.ReactNode; delay?: number }) {
  const ref = useRef<HTMLDivElement>(null);
  const [visible, setVisible] = useState(false);
  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => { if (entry.isIntersecting) setVisible(true); },
      { threshold: 0.08 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, []);
  return (
    <div
      ref={ref}
      className="transition-all duration-700"
      style={{
        opacity: visible ? 1 : 0,
        transform: visible ? 'translateY(0)' : 'translateY(24px)',
        transitionDelay: `${delay}ms`,
      }}
    >
      {children}
    </div>
  );
}

interface JobItemProps {
  label: string;
  children: React.ReactNode;
}

function JobItem({ label, children }: JobItemProps) {
  return (
    <div
      className="grid grid-cols-1 md:grid-cols-4 gap-2 md:gap-8 py-6"
      style={{ borderBottom: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}
    >
      <div
        className="md:col-span-1 font-semibold pt-0.5"
        style={{
          color: '#374151',
          fontFamily: '"Noto Sans JP", sans-serif',
          letterSpacing: '0.04em',
          fontSize: '17px',
        }}
      >
        {label}
      </div>
      <div
        className="md:col-span-3 leading-relaxed"
        style={{ color: '#1A1A1A', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.0', fontSize: '16px' }}
      >
        {children}
      </div>
    </div>
  );
}

export default function RecruitPage() {
  return (
    <SubPageShell>
      <Navbar />

      <SubPageHeader>
          <SectionHeading label="RECRUIT" title="採用情報" as="h1" tone="dark" size="page" />
          <p
            className="mt-6 max-w-xl"
            style={{ fontSize: '17px', color: '#FFFFFF', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.0' }}
          >
            建築を通じて地域社会に貢献する仲間を募集しています。
          </p>
      </SubPageHeader>

      {/* Intro */}
      <SubPageSection spacing="compact">
          <FadeIn>
            <SectionHeading
              label="POSITION"
              title="建築技術者（意匠）"
              tone="dark"
              titleClassName="font-bold"
              titleStyle={{
                fontSize: 'clamp(22px, 3vw, 32px)',
                fontFamily: '"Noto Sans JP", sans-serif',
                letterSpacing: '-0.01em',
                lineHeight: 1.5,
              }}
              className="mb-14"
            />
            <SubPageSurface className="w-full p-8 md:p-10">
              <p
                style={{ fontSize: '17px', color: '#555555', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.2' }}
              >
                建築設計に係る業務全般をお任せします。公共施設から民間施設まで、幅広いプロジェクトに携わりながら、地域の建築文化を支える仕事です。
              </p>
            </SubPageSurface>
          </FadeIn>
      </SubPageSection>

      {/* Job Details */}
      <SubPageSection spacing="compact">
          <FadeIn>
            <SectionHeading label="JOB DETAILS" title="募集要項" tone="dark" className="mb-12" />
          </FadeIn>

          <SubPageSurface className="px-6 md:px-8">
          <div style={{ borderTop: `1px solid ${SUBPAGE_SURFACE_BORDER}` }}>
            <FadeIn delay={50}>
              <JobItem label="仕事内容">
                建築設計に係る業務全般
              </JobItem>
            </FadeIn>

            <FadeIn delay={100}>
              <JobItem label="必要な学歴・資格">
                <ul className="space-y-1.5">
                  <li>高等学校卒業以上</li>
                  <li>普通自動車免許</li>
                  <li>建築設計の経験者</li>
                  <li>一級建築士、二級建築士</li>
                  <li>その他建築・設備関係の資格</li>
                  <li style={{ color: '#4B5563', fontSize: '16px' }}>（建築、設備系の施工管理技士、建築積算士 等）</li>
                </ul>
              </JobItem>
            </FadeIn>

            <FadeIn delay={150}>
              <JobItem label="賃金">
                <div className="space-y-2">
                  <div>
                    <span className="font-semibold" style={{ color: '#0F172A' }}>月給</span>
                    <span className="ml-3" style={{ color: '#1A1A1A' }}>200,000円 〜 250,000円</span>
                  </div>
                  <div>
                    <span className="font-semibold" style={{ color: '#0F172A' }}>昇給</span>
                    <span className="ml-3" style={{ color: '#1A1A1A' }}>1.0 〜 3.0％（業績による）</span>
                  </div>
                  <div>
                    <span className="font-semibold" style={{ color: '#0F172A' }}>賞与</span>
                    <span className="ml-3" style={{ color: '#1A1A1A' }}>年2〜3回（業績による）</span>
                  </div>
                </div>
              </JobItem>
            </FadeIn>

            <FadeIn delay={200}>
              <JobItem label="手当て">
                <div className="space-y-2">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-x-8 gap-y-2">
                    <div>
                      <span className="font-medium" style={{ color: '#0F172A' }}>住宅手当</span>
                      <span className="ml-2" style={{ color: '#4B5563', fontSize: '15px' }}>15,000円（世帯主の場合）</span>
                    </div>
                    <div>
                      <span className="font-medium" style={{ color: '#0F172A' }}>家族手当</span>
                      <span className="ml-2" style={{ color: '#4B5563', fontSize: '15px' }}>5,000円（扶養者がある場合）</span>
                    </div>
                    <div>
                      <span className="font-medium" style={{ color: '#0F172A' }}>技術手当</span>
                      <span className="ml-2" style={{ color: '#4B5563', fontSize: '15px' }}>資格手当、役職手当、技能手当含む 0〜70,000円</span>
                    </div>
                    <div>
                      <span className="font-medium" style={{ color: '#0F172A' }}>残業手当</span>
                      <span className="ml-2" style={{ color: '#4B5563', fontSize: '15px' }}>月平均10〜20時間程度（繁忙期は30時間程度）</span>
                    </div>
                    <div>
                      <span className="font-medium" style={{ color: '#0F172A' }}>通勤手当</span>
                      <span className="ml-2" style={{ color: '#4B5563', fontSize: '15px' }}>上限あり</span>
                    </div>
                  </div>
                </div>
              </JobItem>
            </FadeIn>

            <FadeIn delay={250}>
              <JobItem label="加入保険">
                <div className="space-y-1.5">
                  <div>健康保険、厚生年金、労災保険、雇用保険</div>
                  <div style={{ color: '#4B5563', fontSize: '16px' }}>退職金共済加入（退職金制度なし）</div>
                </div>
              </JobItem>
            </FadeIn>

            <FadeIn delay={300}>
              <JobItem label="定年">
                65歳（再雇用あり）
              </JobItem>
            </FadeIn>

            <FadeIn delay={350}>
              <JobItem label="勤務時間">
                9:00 〜 18:00（休憩1時間）
              </JobItem>
            </FadeIn>

            <FadeIn delay={400}>
              <JobItem label="休日等">
                <div className="space-y-1.5">
                  <div>完全週休二日制（土曜日・日曜日・祝日）</div>
                  <div>夏期休暇、冬期休暇</div>
                  <div>年間休日数：120日程度</div>
                  <div>有給休暇</div>
                </div>
              </JobItem>
            </FadeIn>

            <FadeIn delay={450}>
              <JobItem label="勤務地">
                <div>
                  〒039-1114<br />
                  青森県八戸市北白山台5丁目2-5<br />
                  東奥日報社八戸ビル5階
                </div>
              </JobItem>
            </FadeIn>

            <FadeIn delay={500}>
              <JobItem label="試用期間">
                6ヶ月（労働条件と同じ）
              </JobItem>
            </FadeIn>

            <FadeIn delay={550}>
              <JobItem label="その他">
                新卒、見習い、パートタイムを希望の方は別途相談に応じます。
              </JobItem>
            </FadeIn>
          </div>
          </SubPageSurface>
      </SubPageSection>

      {/* CTA */}
      <SubPageSection spacing="compact">
          <FadeIn>
            <SectionHeading
              label="APPLY"
              title="ご応募・ご相談はお気軽に"
              tone="dark"
              titleClassName="font-bold"
              titleStyle={{
                fontSize: 'clamp(22px, 2.5vw, 30px)',
                fontFamily: '"Noto Sans JP", sans-serif',
                lineHeight: 1.7,
              }}
              className="mb-14"
            />
            <SubPageSurface className="w-full p-8 md:p-10">
              <p
                className="mb-10"
                style={{ fontSize: '17px', color: '#555555', fontFamily: '"Noto Sans JP", sans-serif', lineHeight: '2.2' }}
              >
                ご不明な点やご相談がございましたら、お問い合わせフォームよりお気軽にご連絡ください。
              </p>
              <div className="flex flex-col sm:flex-row gap-4">
                <Link
                  to="/contact"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{
                    background: '#111111',
                    color: '#FFFFFF',
                    fontFamily: '"Inter", sans-serif',
                    letterSpacing: '0.15em',
                    fontSize: '15px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.background = '#333333'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.background = '#111111'; }}
                >
                  お問い合わせフォームへ
                  <i className="ri-arrow-right-line" style={{ fontSize: '18px' }} />
                </Link>
                <a
                  href="tel:0178518296"
                  className="inline-flex items-center justify-center gap-3 px-8 py-4 font-medium transition-all duration-300 cursor-pointer whitespace-nowrap"
                  style={{
                    background: 'transparent',
                    color: '#111111',
                    border: '1px solid #111111',
                    fontFamily: '"Inter", sans-serif',
                    letterSpacing: '0.1em',
                    fontSize: '15px',
                  }}
                  onMouseEnter={(e) => { e.currentTarget.style.borderColor = '#333333'; e.currentTarget.style.color = '#333333'; }}
                  onMouseLeave={(e) => { e.currentTarget.style.borderColor = '#111111'; e.currentTarget.style.color = '#111111'; }}
                >
                  <i className="ri-phone-line" style={{ fontSize: '18px' }} />
                  0178-51-8296
                </a>
              </div>
            </SubPageSurface>
          </FadeIn>
      </SubPageSection>

      <Footer />
    </SubPageShell>
  );
}
